import type Player from 'video.js/dist/types/player';
import { videoIntervalTracker } from './video-interval-tracker';

import {
  sendRemoteEvent,
  RemoteEventType,
  toNumberWithDefaultZero,
} from '~/services/continue-watching/client.ts';
import { type CreateRequest } from '~/services/pipes/create-request';

const CONTINUE_WATCHING_PLUGIN_IDENTIFIER = 'continue-watching';
export const WAIT_FOR_STOPPED_EVENT_BEFORE_NAVIGATION = 2 * 1000;

const getProgress = (time: number, duration: number): number => {
  return duration > 0 ? time / duration : 0;
};

export type ContinueWatchingPluginOptions = {
  entryId: string;
  resumeTime: string;
  entry: any;
  isContinueWatchingSyncedFeed: boolean;
  continueWatchingUserIdentifier: string;
  continueWatchingRemoteEventsRequest: CreateRequest;
  continueWatchingPluginConfiguration: any;
  getIsLive: Function;
  baseURL: string;
};

export default async function continueWatchingPlugin(
  this: Player,
  options: ContinueWatchingPluginOptions
): Promise<void> {
  if (!options?.isContinueWatchingSyncedFeed) return;

  let isListenersAdded = false;
  let isStarted = false;
  let isPaused = false;
  let isCompleted = false;
  let isStopped = false;
  let resumeTime = toNumberWithDefaultZero(options.resumeTime);
  let duration = 0;
  let progress = 0;
  let sessionStartAt = toNumberWithDefaultZero(options.resumeTime);
  let secondsFromStart = '';
  let extraCWData: Record<string, any> = {};
  let heartbeatOnPauseInterval: NodeJS.Timeout;

  const {
    send_heartbeat_on_pause,
    pause_event_heartbeat_interval,

    enable_heartbeat,
    update_frequency: progress_update_frequency,

    enable_video_interval,
    video_interval,
  } = options?.continueWatchingPluginConfiguration || {};

  const updateResumeTime = (
    time: number = toNumberWithDefaultZero(this.currentTime())
  ) => {
    resumeTime = time;
  };

  const updateCompletionStatus = () => {
    const time = toNumberWithDefaultZero(this.currentTime() || resumeTime); // fallback to last known resumeTime before player resets (e.g., resets on top-bar press(:41))
    const isVideoFinished = isVideoCompleted(time, duration);

    if (!duration) return;

    if (isVideoFinished) {
      onComplete();
    } else {
      onRewind();
    }
  };

  // #region Started event
  async function onPlay() {
    extraCWData = {
      ...(options?.entry?.extensions?.[CONTINUE_WATCHING_PLUGIN_IDENTIFIER] ||
        {}),
      ...extraCWData,
    };

    /* update completion status on play */
    updateCompletionStatus();

    if (!satisfyDurationRequirement(duration)) return;

    if (!isStarted && duration) {
      isStarted = true;
      await sendEvent(RemoteEventType.Started);
    }

    if (isPaused) {
      stopHeartbeatOnPause();
      isPaused = false;
    }
  }
  // #endregion

  // #region Paused event, Paused with Heartbeat event
  async function onPause(this: any) {
    if (isPaused) return;

    isPaused = true;

    updateResumeTime();

    const seeking = this.seeking();

    /* update completion status on pause */
    updateCompletionStatus();

    if (seeking) return;

    if (!satisfyDurationRequirement(duration)) return;

    await sendEvent(RemoteEventType.Paused);

    const isHeartbeatOnPause = enable_heartbeat && send_heartbeat_on_pause;

    if (isHeartbeatOnPause) startHeartbeatOnPause();
  }
  // #endregion

  // #region Stopped event
  async function onClose(this: any) {
    if (isStopped) return;
    if (!satisfyDurationRequirement(duration)) return;

    isStopped = true;

    updateResumeTime(this.currentTime() || resumeTime); // fallback to last known resumeTime before player resets (e.g., resets on top-bar press(:41))

    if (isPaused) stopHeartbeatOnPause();

    /* update completion status on close */
    updateCompletionStatus();

    await sendEvent(RemoteEventType.Stopped);

    removeListeners();

    this.trigger('continue-watching:ready-for-dispose-and-navigation');
  }
  // #endregion

  // #region Progress with Heartbeat event
  async function onTimeUpdate(this: any) {
    if (!enable_heartbeat || !progress_update_frequency) return;

    const time = this.currentTime();
    const duration = this.duration();

    secondsFromStart = String(time);
    progress = getProgress(time, duration);

    /* update completion status on time update */
    if (isVideoCompleted(time, duration)) {
      onComplete();
    } else {
      onRewind();
    }

    if (isPaused) return;

    if (isCompleted || !isVideoTrackable(time)) return;

    const isProgressUpdateFrequencyReached =
      Math.abs(Number(time) - (resumeTime || 0)) >=
      Number(progress_update_frequency);

    if (!isProgressUpdateFrequencyReached) return;

    updateResumeTime();

    await sendEvent(RemoteEventType.Progress, {
      currentProgress: progress,
    });
  }
  // #endregion

  /**
   * Checks if the video duration meets the minimum duration requirement
   * @param duration video duration from the player
   * @returns
   */
  function satisfyDurationRequirement(duration: number) {
    return (
      duration >
      (options?.continueWatchingPluginConfiguration
        ?.minimum_auto_resume_content_duration ?? 0)
    );
  }

  /**
   * Checks if the video should be marked as completed
   * @param time current playback time in seconds or resumeTime (when player is stopped)
   * @param duration video duration from the player
   * @returns boolean value
   */
  function isVideoCompleted(time: number, duration: number) {
    const completionThreshold = Math.min(
      options?.continueWatchingPluginConfiguration
        ?.auto_resume_completion_threshold ?? 0,
      duration
    );
    return time >= duration - completionThreshold;
  }

  /**
   * Checks if the video progress should be tracked for heartbeat events
   * @param time current playback time in seconds or resumeTime (when player is stopped)
   * @returns boolean value
   */
  function isVideoTrackable(time: number) {
    return (
      satisfyDurationRequirement(duration) && !isVideoCompleted(time, duration)
    );
  }

  function startHeartbeatOnPause() {
    heartbeatOnPauseInterval = setInterval(async () => {
      await sendEvent(RemoteEventType.Paused);
    }, pause_event_heartbeat_interval * 1000);
  }

  function stopHeartbeatOnPause() {
    clearInterval(heartbeatOnPauseInterval);
  }

  function onComplete() {
    if (!isCompleted) isCompleted = true;
  }

  function onRewind() {
    if (isCompleted) isCompleted = false;
  }

  function onLoadedMetadata(this: any) {
    this.currentTime(resumeTime);
    duration = toNumberWithDefaultZero(this.player().duration());
    progress = toNumberWithDefaultZero(getProgress(resumeTime, duration));

    if (satisfyDurationRequirement(duration) && enable_video_interval) {
      // #region Interval event
      videoIntervalTracker(this, video_interval, sendEvent);
      // #endregion
    }
  }

  async function sendEvent(eventType: RemoteEventType, eventOptions: any = {}) {
    if (!options.isContinueWatchingSyncedFeed) return;

    if (options.getIsLive() && eventType !== RemoteEventType.VideoInterval)
      return;

    if (isCompleted) {
      if (eventType === RemoteEventType.Progress) return;
      if (eventType === RemoteEventType.Paused) return;
    }

    const { currentProgress, ...remoteEventOptions } = eventOptions;
    const progressValue = toNumberWithDefaultZero(
      currentProgress || getProgress(resumeTime, duration)
    );

    const continueWatchingRemoteEventPayload = {
      item: options.entry,
      eventType: eventType,
      videoId: options.entryId,
      progress: progressValue,
      secondsFromStart: secondsFromStart || String(progressValue * duration),
      sessionStartAt,
      extraCWData: {
        ...extraCWData,
        ...remoteEventOptions,
      },
      baseURL: options.baseURL,
      continueWatchingUserIdentifier: options.continueWatchingUserIdentifier,
      continueWatchingRemoteEventsRequest:
        options.continueWatchingRemoteEventsRequest,
      removeItemFromIndexedDB: isCompleted,
    };

    const remoteEventResponse = await sendRemoteEvent(
      continueWatchingRemoteEventPayload
    );

    extraCWData = {
      ...extraCWData,
      ...remoteEventResponse?.[CONTINUE_WATCHING_PLUGIN_IDENTIFIER],
    };
  }

  // #region Listeners
  const handleBeforeUnload = () => {
    onClose.call(this);
  };

  const removeListeners = () => {
    if (!isListenersAdded) return;
    isListenersAdded = false;
    window.removeEventListener('beforeunload', handleBeforeUnload);
    this.off('pause', onPause);
    this.off('play', onPlay);
    this.off('loadedmetadata', onLoadedMetadata);
    this.off('timeupdate', onTimeUpdate);
    this.off('ended', onComplete);
  };

  const addListeners = () => {
    if (isListenersAdded) return;
    isListenersAdded = true;
    this.on('continue-watching:send-stopped-event', onClose);
    window.addEventListener('beforeunload', handleBeforeUnload);
    this.on('pause', onPause);
    this.on('play', onPlay);
    this.on('loadedmetadata', onLoadedMetadata);
    this.on('timeupdate', onTimeUpdate);
    this.on('ended', onComplete);
  };
  // #endregion

  addListeners();
}
